import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { objectsApi, relationsApi } from '@/lib/api';
import type { CreateObjectRequest, UpdateObjectRequest, ListObjectsParams } from '@arkitekt/shared';

export function useObjects(spaceId: string | undefined, params?: ListObjectsParams) {
  return useQuery({
    queryKey: ['objects', spaceId, params],
    queryFn: () => objectsApi.list(spaceId!, params),
    enabled: !!spaceId,
  });
}

export function useObject(spaceId: string | undefined, objectId: string | undefined) {
  return useQuery({
    queryKey: ['object', spaceId, objectId],
    queryFn: () => objectsApi.get(spaceId!, objectId!),
    enabled: !!spaceId && !!objectId,
  });
}

export function useCreateObject(spaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateObjectRequest) => objectsApi.create(spaceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objects', spaceId] });
    },
  });
}

export function useUpdateObject(spaceId: string, objectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateObjectRequest) => objectsApi.update(spaceId, objectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objects', spaceId] });
      queryClient.invalidateQueries({ queryKey: ['object', spaceId, objectId] });
    },
  });
}

export function useDeleteObject(spaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (objectId: string) => objectsApi.delete(spaceId, objectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objects', spaceId] });
    },
  });
}

export function useObjectRelations(
  spaceId: string | undefined,
  objectId: string | undefined,
  direction: 'incoming' | 'outgoing' | 'both' = 'both'
) {
  return useQuery({
    queryKey: ['relations', spaceId, objectId, direction],
    queryFn: () => relationsApi.list(spaceId!, objectId!, direction),
    enabled: !!spaceId && !!objectId,
  });
}
